import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ChevronLeft, ChevronRight, Plus, CalendarDays, Trash2, DollarSign, Bell, Users as UsersIcon, Clock, Pencil, AlertTriangle } from 'lucide-react';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const TYPE_META = {
  pago: { label: 'Factura a crédito', color: '#DC2626', bg: '#FEF2F2', icon: DollarSign },
  evento: { label: 'Evento', color: '#022C22', bg: '#ECFDF5', icon: CalendarDays },
  recordatorio: { label: 'Recordatorio', color: '#D97706', bg: '#FFFBEB', icon: Bell },
  reunion: { label: 'Reunión', color: '#2563EB', bg: '#EFF6FF', icon: UsersIcon },
  entrega: { label: 'Entrega', color: '#7C3AED', bg: '#F5F3FF', icon: Clock },
};
const CREDIT_META = {
  vencida: { label: 'Vencida', color: '#DC2626', bg: '#FEF2F2' },
  por_vencer: { label: 'Por vencer (≤7 días)', color: '#D97706', bg: '#FFFBEB' },
  pendiente: { label: 'A crédito (al día)', color: '#0891B2', bg: '#ECFEFF' },
};
const eventMeta = (e) => {
  if (e.source === 'credit_invoice') {
    const m = CREDIT_META[e.credit_status] || CREDIT_META.pendiente;
    return { ...m, icon: DollarSign };
  }
  return TYPE_META[e.type] || TYPE_META.evento;
};
const money = (n) => `$${Number(n || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inputCls = 'w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#022C22]/20';

export default function Calendar() {
  const { user } = useAuth();
  const isClient = user?.role_id === 'cliente';
  const isAdmin = ['master', 'superadmin', 'admin'].includes(user?.role_id);
  const isMaster = ['master', 'superadmin'].includes(user?.role_id);

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', date: '', type: 'evento', description: '', time: '' });
  const [editingId, setEditingId] = useState(null);
  const [creditEdit, setCreditEdit] = useState(null);
  const [sendingRem, setSendingRem] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => { if (isMaster) api.getCompanies().then((r) => setCompanies(r.data || [])).catch(() => {}); }, [isMaster]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getCalendarEvents(year, month + 1, isMaster ? (companyId || undefined) : undefined);
      setEvents(r.data || []);
    } catch { toast.error('No se pudo cargar el calendario'); }
    finally { setLoading(false); }
  }, [year, month, companyId, isMaster]);
  useEffect(() => { load(); }, [load]);

  const pad = (n) => String(n).padStart(2, '0');
  const dateStr = (d) => `${year}-${pad(month + 1)}-${pad(d)}`;
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const eventsByDay = {};
  events.forEach((e) => { (eventsByDay[e.date] = eventsByDay[e.date] || []).push(e); });

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear((y) => y - 1); } else setMonth((m) => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear((y) => y + 1); } else setMonth((m) => m + 1); };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  const openNew = (d) => {
    if (isClient) return;
    setEditingId(null);
    setForm({ title: '', date: d || todayStr, type: 'evento', description: '', time: '' });
    setShowForm(true);
  };
  const openEdit = (e) => {
    if (e.source === 'credit_invoice') { setCreditEdit({ ...e }); return; }
    setEditingId(e.event_id);
    setForm({ title: e.title, date: e.date, type: e.type, description: e.description || '', time: e.time || '' });
    setShowForm(true);
  };
  const saveEvent = async () => {
    if (!form.title.trim() || !form.date) return toast.error('Título y fecha son obligatorios');
    try {
      const payload = { ...form, company_id: isMaster ? (companyId || undefined) : undefined };
      if (editingId) await api.updateCalendarEvent(editingId, payload);
      else await api.createCalendarEvent(payload);
      toast.success('Evento guardado'); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
  };
  const removeEvent = async (id) => {
    try { await api.deleteCalendarEvent(id); toast.success('Evento eliminado'); load(); }
    catch { toast.error('Error al eliminar'); }
  };
  const saveCredit = async () => {
    try {
      await api.updateSaleCreditInfo(creditEdit.sale_id, { due_date: creditEdit.date, credit_observation: creditEdit.description });
      toast.success('Vencimiento actualizado'); setCreditEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const sendReminders = async () => {
    setSendingRem(true);
    try { const r = await api.sendCreditReminders(); toast.success(`Recordatorios enviados: ${r.data.sent}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al enviar recordatorios'); }
    finally { setSendingRem(false); }
  };
  const registerPay = async () => {
    const amt = Number(payAmount);
    if (!amt || amt <= 0) return toast.error('Ingresa un monto válido');
    setPaying(true);
    try { await api.registerPayment(creditEdit.sale_id, amt); toast.success('Pago registrado ✅'); setPayAmount(''); setCreditEdit(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al registrar pago'); }
    finally { setPaying(false); }
  };

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const creditEvents = events.filter((e) => e.source === 'credit_invoice');
  const totalCredit = creditEvents.reduce((a, e) => a + Number(e.amount || 0), 0);
  const dayEvents = selectedDay ? (eventsByDay[selectedDay] || []) : [];

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22] flex items-center gap-2">
            <CalendarDays /> {isClient ? 'Mis Vencimientos' : 'Calendario'}
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            {isClient ? 'Fechas de vencimiento de tus facturas a crédito' : 'Agenda global: eventos, recordatorios y facturas a crédito por vencer'}
          </p>
        </div>
        {!isClient && (
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button variant="outline" onClick={sendReminders} disabled={sendingRem} data-testid="calendar-send-reminders">
                <Bell size={15} className="mr-1" /> {sendingRem ? 'Enviando…' : 'Enviar recordatorios'}
              </Button>
            )}
            <Button onClick={() => openNew()} data-testid="calendar-new-event" className="bg-[#022C22] hover:bg-[#033a2e]">
              <Plus size={16} className="mr-1" /> Nuevo evento
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevMonth} data-testid="calendar-prev"><ChevronLeft size={18} /></Button>
          <div className="text-lg font-semibold text-[#022C22] min-w-[190px] text-center" data-testid="calendar-month-label">{MESES[month]} {year}</div>
          <Button variant="outline" size="icon" onClick={nextMonth} data-testid="calendar-next"><ChevronRight size={18} /></Button>
          <Button variant="ghost" onClick={goToday} className="text-[#022C22]" data-testid="calendar-today">Hoy</Button>
        </div>
        <div className="flex items-center gap-3">
          {isMaster && (
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className={inputCls + ' max-w-[220px]'} data-testid="calendar-company">
              <option value="">Todas las empresas</option>
              {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
            </select>
          )}
          {!isClient && creditEvents.length > 0 && (
            <div className="text-sm bg-[#FEF2F2] text-[#DC2626] font-semibold px-3 py-1.5 rounded-lg flex items-center gap-1.5" data-testid="calendar-credit-total">
              <AlertTriangle size={15} /> {creditEvents.length} por cobrar · {money(totalCredit)}
            </div>
          )}
        </div>
      </div>

      <Card className="border border-[#E2E8F0] overflow-hidden">
        <CardContent className="p-0">
          <div className="grid grid-cols-7 bg-[#022C22] text-white text-xs font-semibold">
            {DIAS.map((d) => <div key={d} className="py-2.5 text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const ds = d ? dateStr(d) : null;
              const evs = ds ? (eventsByDay[ds] || []) : [];
              const isToday = ds === todayStr;
              return (
                <div
                  key={i}
                  onClick={() => d && setSelectedDay(ds)}
                  data-testid={d ? `calendar-day-${d}` : undefined}
                  className={`min-h-[92px] border-b border-r border-[#F1F5F9] p-1.5 ${d ? 'cursor-pointer hover:bg-[#F8FAFC]' : 'bg-[#FAFAFA]'} transition-colors`}
                >
                  {d && (
                    <>
                      <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-[#022C22] text-white' : 'text-[#334155]'}`}>{d}</div>
                      <div className="space-y-1">
                        {evs.slice(0, 3).map((e, idx) => {
                          const meta = eventMeta(e);
                          return (
                            <div key={idx} className="text-[10px] leading-tight truncate px-1.5 py-0.5 rounded" style={{ background: meta.bg, color: meta.color }} title={e.title}>
                              {e.source === 'credit_invoice' ? `${money(e.amount)} · ${e.client_name || ''}` : e.title}
                            </div>
                          );
                        })}
                        {evs.length > 3 && <div className="text-[10px] text-[#64748B] px-1">+{evs.length - 3} más</div>}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-3 text-xs text-[#64748B]">
        {Object.entries(CREDIT_META).map(([k, m]) => (
          <div key={k} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: m.color }} /> {m.label}</div>
        ))}
        {['evento', 'recordatorio', 'reunion', 'entrega'].map((k) => (
          <div key={k} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: TYPE_META[k].color }} /> {TYPE_META[k].label}</div>
        ))}
      </div>

      {/* Detalle del día */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-lg" data-testid="calendar-day-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#022C22]">{selectedDay && new Date(selectedDay + 'T00:00:00').toLocaleDateString('es-VE', { weekday: 'long', day: 'numeric', month: 'long' })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {dayEvents.length === 0 && <p className="text-sm text-[#94A3B8] py-4 text-center">Sin eventos este día</p>}
            {dayEvents.map((e, idx) => {
              const meta = eventMeta(e);
              const Icon = meta.icon;
              return (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border border-[#E2E8F0]" style={{ background: meta.bg }} data-testid={`day-event-${idx}`}>
                  <Icon size={18} style={{ color: meta.color }} className="mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-[#1E293B]">{e.title}</div>
                    {e.source === 'credit_invoice' && (
                      <div className="text-xs text-[#475569] mt-0.5">
                        <b style={{ color: meta.color }}>{money(e.amount)}</b> · {e.client_name} · {e.document_type === 'nota_entrega' ? 'Nota de entrega' : 'Factura'}
                      </div>
                    )}
                    {e.time && <div className="text-xs text-[#64748B]">🕐 {e.time}</div>}
                    {e.description && <div className="text-xs text-[#64748B] mt-1 whitespace-pre-wrap">{e.description}</div>}
                    {e.created_by_name && <div className="text-[10px] text-[#94A3B8] mt-1">por {e.created_by_name}</div>}
                  </div>
                  {e.editable && !isClient && (
                    <div className="flex flex-col gap-1">
                      <button onClick={() => openEdit(e)} className="text-[#022C22] hover:bg-white/60 rounded p-1" data-testid={`edit-event-${idx}`}><Pencil size={14} /></button>
                      {e.source === 'manual' && <button onClick={() => removeEvent(e.event_id)} className="text-[#DC2626] hover:bg-white/60 rounded p-1" data-testid={`delete-event-${idx}`}><Trash2 size={14} /></button>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {!isClient && (
            <DialogFooter>
              <Button onClick={() => { openNew(selectedDay); }} className="bg-[#022C22] hover:bg-[#033a2e]" data-testid="day-add-event"><Plus size={15} className="mr-1" /> Agregar aquí</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Formulario evento manual */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md" data-testid="event-form-dialog">
          <DialogHeader><DialogTitle className="text-[#022C22]">{editingId ? 'Editar evento' : 'Nuevo evento'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="event-title" placeholder="Ej: Pago de proveedor" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="event-date" /></div>
              <div><Label>Hora (opcional)</Label><Input type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} data-testid="event-time" /></div>
            </div>
            <div>
              <Label>Tipo</Label>
              <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className={inputCls} data-testid="event-type">
                <option value="evento">Evento</option>
                <option value="recordatorio">Recordatorio</option>
                <option value="reunion">Reunión</option>
                <option value="entrega">Entrega</option>
              </select>
            </div>
            <div><Label>Observaciones</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} data-testid="event-desc" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={saveEvent} className="bg-[#022C22] hover:bg-[#033a2e]" data-testid="event-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar vencimiento de factura a crédito */}
      <Dialog open={!!creditEdit} onOpenChange={(o) => !o && setCreditEdit(null)}>
        <DialogContent className="max-w-md" data-testid="credit-edit-dialog">
          <DialogHeader><DialogTitle className="text-[#DC2626] flex items-center gap-2"><DollarSign size={18} /> Factura a crédito</DialogTitle></DialogHeader>
          {creditEdit && (
            <div className="space-y-3">
              <div className="bg-[#FEF2F2] rounded-lg p-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-[#1E293B]">{creditEdit.invoice_number}</div>
                  {creditEdit.credit_status && (
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: (CREDIT_META[creditEdit.credit_status] || {}).bg, color: (CREDIT_META[creditEdit.credit_status] || {}).color }}>
                      {(CREDIT_META[creditEdit.credit_status] || {}).label}
                    </span>
                  )}
                </div>
                <div className="text-[#475569] mt-1">{creditEdit.client_name} · <b className="text-[#DC2626]">{money(creditEdit.amount)}</b> por cobrar</div>
              </div>
              <div><Label>Fecha de vencimiento</Label><Input type="date" value={creditEdit.date} onChange={(e) => setCreditEdit((c) => ({ ...c, date: e.target.value }))} data-testid="credit-due-date" /></div>
              <div><Label>Observaciones de cobranza</Label><Textarea value={creditEdit.description || ''} onChange={(e) => setCreditEdit((c) => ({ ...c, description: e.target.value }))} rows={3} data-testid="credit-obs" placeholder="Ej: Cliente abona el día 15" /></div>
              {isAdmin && (
                <div className="border-t border-[#F1F5F9] pt-3">
                  <Label className="flex items-center gap-1 text-[#022C22]"><DollarSign size={14} /> Registrar pago / abono</Label>
                  <div className="flex gap-2 mt-1">
                    <Input type="number" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="Monto del abono" data-testid="credit-pay-amount" />
                    <Button variant="outline" onClick={() => setPayAmount(String(creditEdit.amount))} data-testid="credit-pay-all">Todo</Button>
                    <Button onClick={registerPay} disabled={paying} className="bg-green-700 hover:bg-green-800 text-white" data-testid="credit-pay-btn">{paying ? '…' : 'Pagar'}</Button>
                  </div>
                  <p className="text-[10px] text-[#94A3B8] mt-1">Al pagar el total, la factura sale del calendario automáticamente.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditEdit(null)}>Cancelar</Button>
            <Button onClick={saveCredit} className="bg-[#DC2626] hover:bg-[#b91c1c]" data-testid="credit-save">Guardar vencimiento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
